import type { AccountGroupType } from '../account-groups/account-group.dto';
import type { ProductType } from '../products';
import { COVERAGE_STATUS, type CoverageStatus } from './CoverageStatus';

export const CLAIM_STATUS_DATE_KEY_MAP = {
  [COVERAGE_STATUS.DENIED]: 'deniedDate',
  [COVERAGE_STATUS.PENDING]: 'pendingDate',
  [COVERAGE_STATUS.NOTICE_ONLY]: 'noticeOnlyDate',
  [COVERAGE_STATUS.PROVIDED]: 'providedDate',
  [COVERAGE_STATUS.PROVIDED_IN_PART_OR_DENIED_IN_PART]:
    'providedInPartOrDeniedInPartDate',
} as const;

type ClaimStatusDateKey = (typeof CLAIM_STATUS_DATE_KEY_MAP)[CoverageStatus];

export type ClaimDto = {
  accountGroupName?: string;
  accountGroupType?: AccountGroupType;
  accountId: string;
  agencyId: string;
  agencyName: string;
  agentEmail: string;
  agentFirstName: string;
  agentLastName: string;
  claimNumber: string;
  /** The day the claim was closed; the service sends it only on a closed claim. */
  closedDate?: string | null;
  companyName: string;
  created: string;
  /** Full name of the person who filed the claim. */
  createdBy: string;
  /** Email of the person who filed the claim. */
  createdByEmail?: string | null;
  customerEmail: string;
  customerFirstName: string;
  customerLastName: string;
  customerPhone: string;
  coverageInfo?: {
    coverage?: CoverageStatus;
    coverageCounsel?: boolean;
    subrogationPotential?: boolean;
    subrogationStartDate?: string;
    coverageDate?: Partial<Record<ClaimStatusDateKey, string>>;
    claimAcknowledgementDate?: string;
  };
  patientZeroInfo?: {
    caseCategory?: string;
    projectCodeName?: string;
    threatActor?: string;
    initialAttackVector?: string;
    cvesLeveraged?: string[];
    impactedTechnology?: string;
    notes?: string;
    capturedDate?: string;
    capturedBy?: string;
    source?: 'MANUAL' | 'AUTO_FROM_VENDOR';
  };
  description: string;
  effectiveDate: string;
  endDate: string;
  handlerEmail: string;
  handlerFirstName: string;
  handlerId: string;
  handlerImageUrl: string;
  handlerLastName: string;
  handlerPhone: string;
  id: string;
  incidentDate: string;
  incidentType: string;
  isThereAnotherClaim: boolean;
  latestDueDate: string;
  limit: number;
  modified: string;
  policyId: string;
  policyNumber: string;
  primaryIndustry: string;
  productType: ProductType;
  reportedDate: string;
  retention: number;
  revenue: number;
  severity: string;
  state: string;
  status: ClaimStatus;
  teamIds: string[];
  totalPremium: number;
  vendorIds: string[];
};

export enum ClaimStatus {
  FNOL = 'FNOL',
  OPEN = 'OPEN',
  CLOSED = 'CLOSED',
  NOTICE_ONLY = 'NOTICE ONLY',
}

export const CLAIM_DOC_TYPE = {
  Claim: 'Claim',
  Claim_Acknowledgement_Letter: 'Claim_Acknowledgement_Letter',
  Claim_Complaint: 'Claim_Complaint',
  Claim_Coverage_Letter: 'Claim_Coverage_Letter',
  Claim_Email: 'Claim_Email',
  Claim_External_Correspondence: 'Claim_External_Correspondence',
  Claim_Image: 'Claim_Image',
  Claim_Internal_Correspondence: 'Claim_Internal_Correspondence',
  Claim_Invoice: 'Claim_Invoice',
  Claim_Order: 'Claim_Order',
  Claim_Other: 'Claim_Other',
  Claim_Report: 'Claim_Report',
} as const;

export type ClaimDocType = (typeof CLAIM_DOC_TYPE)[keyof typeof CLAIM_DOC_TYPE];
